<script lang="ts">
  import ErrorCard from './ErrorCard.svelte'
  import AIActionCard from './AIActionCard.svelte'
  import BulkPromptInput from './BulkPromptInput.svelte'
  import AIActionsHeader from './AIActionsHeader.svelte'
  import type { GrammarError } from '$lib/types/grammar'
  import type { Segment } from '$lib/stores/aiFeatures'
  import { settingsStore, settingsActions } from '$lib/stores/settings'
  import { AI_MODELS } from '$lib/config/aiModels.config'
  import { DiffRenderer } from '$lib/services/DiffRenderer'
  import { CheckCircle, Info, Menu, Check, X } from 'lucide-svelte'
  
  // Import controllers
  import AICardController from './controllers/AICardController.svelte'
  import ErrorCardController from './controllers/ErrorCardController.svelte'
  import ErrorFocusController from './controllers/ErrorFocusController.svelte'
  import CompletionController from './controllers/CompletionController.svelte'
  import StatsController from './controllers/StatsController.svelte'
  
  interface Props {
    errors: GrammarError[]
    ignoredErrors: Set<string>
    isChecking: boolean
    hasChecked: boolean
    hasText: boolean
    allApplied?: boolean
    processedCount?: number
    initialErrorCount?: number
    onApplyCorrection: (errorId: string, correctionId: string) => void
    onApplyAlternative?: (errorId: string, alternative: string) => void
    onIgnoreError: (errorId: string) => void
    onKeepOriginal?: (errorId: string) => void
    onApplyAll?: () => void
    onRejectAll?: () => void
    onCancelCheck?: () => void
    onErrorHover?: (errorId: string) => void
    onErrorUnhover?: () => void
    // AI mode props
    isAIMode?: boolean
    aiSegments?: Segment[]
    aiMode?: 'paraphrase' | 'expand' | 'shrink'
    isAIAnalyzing?: boolean
    onAIApply?: (segmentId: string) => void
    onAIIgnore?: (segmentId: string) => void
    onAIContinue?: (segmentId: string) => void
    onAIExpand?: (segmentId: string) => void
    onAIShrink?: (segmentId: string) => void
    onAIParaphrase?: (segmentId: string) => void
    onAIPrompt?: (segmentId: string) => void
    onAISegmentPromptGenerate?: (segmentId: string, prompt: string, modelId: string) => void
    onAIApplyAll?: () => void
    onAIRejectAll?: () => void
    onAIApplyAndExit?: () => void
    onAIContinueEditing?: () => void
    onAIContinueAll?: () => void
    onAIParaphraseAll?: () => void
    onAIExpandAll?: () => void
    onAIShrinkAll?: () => void
    onAIPromptAll?: (prompt?: string, modelId?: string) => void
    onSegmentHover?: (segmentId: string | null) => void
    onSegmentUndo?: (segmentId: string) => void
    onSegmentRedo?: (segmentId: string) => void
    canSegmentUndo?: (segmentId: string) => boolean
    canSegmentRedo?: (segmentId: string) => boolean
    // Inline view props
    inlineViewCards?: any[]
    onInlineViewApply?: (viewId: string) => void
    onInlineViewIgnore?: (viewId: string) => void
    onInlineViewParaphrase?: (viewId: string) => void
    onInlineViewExpand?: (viewId: string) => void
    onInlineViewShrink?: (viewId: string) => void
    onInlineViewContinue?: (viewId: string) => void
    onInlineViewPrompt?: (viewId: string) => void
    onInlineViewGeneratePrompt?: (viewId: string, prompt: string, modelId: string) => void
    onInlineViewUndo?: (viewId: string) => void
    onInlineViewRedo?: (viewId: string) => void
    canInlineViewUndo?: (viewId: string) => boolean
    canInlineViewRedo?: (viewId: string) => boolean
  }
  
  let { 
    errors, 
    ignoredErrors,
    isChecking,
    hasChecked,
    hasText,
    allApplied = false,
    processedCount = 0,
    initialErrorCount = 0,
    onApplyCorrection,
    onApplyAlternative,
    onIgnoreError,
    onKeepOriginal,
    onApplyAll,
    onRejectAll,
    onCancelCheck,
    onErrorHover,
    onErrorUnhover,
    // AI mode props
    isAIMode = false,
    aiSegments = [],
    aiMode = 'paraphrase',
    isAIAnalyzing = false,
    onAIApply,
    onAIIgnore,
    onAIContinue,
    onAIExpand,
    onAIShrink,
    onAIParaphrase,
    onAIPrompt,
    onAISegmentPromptGenerate,
    onAIApplyAll,
    onAIRejectAll,
    onAIApplyAndExit,
    onAIContinueEditing,
    onAIContinueAll,
    onAIParaphraseAll,
    onAIExpandAll,
    onAIShrinkAll,
    onAIPromptAll,
    onSegmentHover,
    onSegmentUndo,
    onSegmentRedo,
    canSegmentUndo,
    canSegmentRedo,
    // Inline view props
    inlineViewCards = [],
    onInlineViewApply,
    onInlineViewIgnore,
    onInlineViewParaphrase,
    onInlineViewExpand,
    onInlineViewShrink,
    onInlineViewContinue,
    onInlineViewPrompt,
    onInlineViewGeneratePrompt,
    onInlineViewUndo,
    onInlineViewRedo,
    canInlineViewUndo,
    canInlineViewRedo
  }: Props = $props()
  
  // Controller references
  let aiCardController: AICardController
  let errorCardController: ErrorCardController
  let errorFocusController: ErrorFocusController
  let completionController: CompletionController
  let statsController: StatsController
  
  // Container element for scrolling
  let errorListContainer = $state<HTMLDivElement | null>(null)
  
  // Bulk prompt state
  let showBulkPromptInput = $state(false)
  let bulkPromptInput = $state('')
  let savedBulkPromptInput = '' // Save bulk prompt input when closing without submitting
  let showModelDropdown = $state(false)
  let modelSelectorRef = $state<HTMLDivElement | null>(null)
  
  // Get settings reactively
  const settings = $derived($settingsStore)
  
  // Get selected AI model from settings store - use state for binding
  let currentModelId = $state($settingsStore.ai?.selectedModel || 'openai/gpt-oss-120b')
  
  // Keep model ID in sync with settings
  $effect(() => {
    currentModelId = settings.ai?.selectedModel || 'openai/gpt-oss-120b'
  })
  
  
  // Debug AI handlers - removed to reduce console noise
  
  // Get all available AI models from configuration
  let allAIModels = $derived.by(() => {
    const models = []
    const providers = settings.aiProviders || []
    
    // Get enabled providers
    const enabledProviders = providers.filter(p => p.enabled)
    
    // Add models from each enabled provider
    for (const provider of enabledProviders) {
      const providerModels = AI_MODELS[provider.id] || []
      models.push(...providerModels)
    }
    
    // If OpenRouter is not enabled, still include it (free models)
    if (!enabledProviders.some(p => p.id === 'openrouter')) {
      const openrouterModels = AI_MODELS['openrouter'] || []
      models.push(...openrouterModels)
    }
    
    return models
  })
  
  let currentAIModel = $derived(
    allAIModels.find(m => m.id === currentModelId) || 
    allAIModels.find(m => m.id === 'openai/gpt-oss-120b') || // Default to OpenRouter GPT-OSS
    null
  )
  
  // Get data from controllers - will update when controller changes
  // Fallback to direct calculation if controller not ready
  let activeErrors = $derived(
    errorCardController?.getActiveErrors() || errors.filter(error => !ignoredErrors.has(error.id))
  )
  let hasActiveErrors = $derived(
    errorCardController?.getHasActiveErrors() ?? activeErrors.length > 0
  )
  // Errors that will be displayed (not ignored, including kept-original)
  let displayableErrors = $derived(
    errors.filter(error => !ignoredErrors.has(error.id) || error.isOriginalKept || error.userAction === 'kept-original')
  )
  let displayState = $derived(errorCardController?.getDisplayState() || 'ready')
  
  // Check if there are non-AI grammar errors (from grammar check)
  let hasGrammarCheckErrors = $derived(
    errors.some(error => !error.isAIOnly && !error.isAIGenerated)
  )
  
  
  // Compute emptyStateMessage based on displayState to ensure it updates
  let emptyStateMessage = $derived((() => {
    if (!errorCardController) return null
    // Force re-compute when displayState changes
    const currentState = displayState
    return errorCardController.getEmptyStateMessage()
  })())
  
  let activeSegments = $derived(aiCardController?.getActiveSegments() || [])
  let hasUnprocessedSegments = $derived(aiCardController?.getHasUnprocessedSegments() || false)
  let hasAppliedSegments = $derived(aiCardController?.getHasAppliedSegments() || false)
  let isProcessing = $derived(aiCardController?.getIsSegmentProcessing() || false)
  
  // Force reactivity by accessing aiSegments in derived
  let showCompletionMessage = $derived.by(() => {
    // Access aiSegments to trigger reactivity
    const segmentsCount = aiSegments?.length || 0
    return completionController?.isShowingCompletionMessage() || false
  })
  let showApplyMessage = $derived.by(() => {
    const segmentsCount = aiSegments?.length || 0
    return completionController?.isShowingApplyMessage() || false
  })
  let appliedCount = $derived.by(() => {
    const segmentsCount = aiSegments?.length || 0
    return completionController?.getAppliedCount() || 0
  })
  let skippedCount = $derived.by(() => {
    const segmentsCount = aiSegments?.length || 0
    return completionController?.getSkippedCount() || 0
  })
  
  let totalStats = $derived.by(() => {
    return statsController?.getTotalStats() || { additions: 0, deletions: 0 }
  })
  
  // Calculate inline view stats
  const diffRenderer = new DiffRenderer()
  
  let inlineViewStats = $derived.by(() => {
    let additions = 0
    let deletions = 0
    
    inlineViewCards?.forEach(view => {
      if (view.diff) {
        // Use existing diff if available
        additions += view.diff.additions || 0
        deletions += view.diff.deletions || 0
      } else if (view.segments && view.segments[0]) {
        const segment = view.segments[0]
        if (segment.original && segment.suggested && segment.suggested !== segment.original) {
          // Calculate actual diff using DiffRenderer
          const stats = diffRenderer.getDiffStats(segment.original, segment.suggested)
          additions += stats.additions
          deletions += stats.deletions
        }
      }
    })
    
    return { additions, deletions }
  })
  
  // Helper function to check if sidebar should auto-close
  function checkAndCloseSidebar() {
    setTimeout(() => {
      const activeViews = inlineViewCards?.filter(
        v => v.status !== 'applied' && v.status !== 'cancelled'
      ) || []
      
      if (activeViews.length === 0 && errors.length === 0 && !isAIMode) {
        // No more content, close sidebar
        import('$lib/stores/sidebarStore').then(({ sidebarStore }) => {
          sidebarStore.close(true) // true = auto-close
        })
      }
    }, 200)
  }
  
  // Tips to show users
  const tips = [
    {
      title: '맞춤법 검사를 시작하세요',
      subtitle: '⌘+Shift+G 또는 아래 버튼을 클릭하여 검사'
    },
    {
      title: 'AI로 문장을 개선해보세요',
      subtitle: '왼쪽 도구모음에서 다양한 AI 기능 사용 가능'
    },
    {
      title: '문장을 이어서 작성해보세요',
      subtitle: 'Shift+Tab으로 AI가 다음 문장 제안'
    },
    {
      title: '선택 영역만 검사할 수 있습니다',
      subtitle: '텍스트를 선택한 후 맞춤법 검사 실행'
    },
    {
      title: '다양한 AI 기능을 활용해보세요',
      subtitle: '바꿔쓰기, 늘려쓰기, 줄여쓰기, 요약하기 등'
    },
    {
      title: '모든 교정을 한 번에 적용하세요',
      subtitle: '⌘+Shift+Enter로 모든 오류 수정'
    },
    {
      title: 'AI가 문장을 분석해드립니다',
      subtitle: '⌘+Shift+A로 AI 문법 분석 실행'
    },
    {
      title: '실행 취소도 가능합니다',
      subtitle: '⌘+Z로 이전 상태로 되돌리기'
    }
  ]
  
  // Select random tip index on component mount
  let currentTipIndex = $state(Math.floor(Math.random() * tips.length))
  
  // Rotate tip when text changes from empty to having text
  let previousHasText = $state(false)
  $effect(() => {
    if (hasText && !previousHasText) {
      currentTipIndex = (currentTipIndex + 1) % tips.length
    }
    previousHasText = hasText
  })
  
  // Bulk prompt functions
  function handleBulkPromptClick() {
    if (showBulkPromptInput) {
      // Save input before closing
      if (bulkPromptInput.trim()) {
        savedBulkPromptInput = bulkPromptInput
      }
      showBulkPromptInput = false
    } else {
      // Restore saved input when opening
      if (savedBulkPromptInput) {
        bulkPromptInput = savedBulkPromptInput
      }
      showBulkPromptInput = true
    }
  }
  
  // Close bulk prompt
  function closeBulkPrompt() {
    // Save input before closing
    if (bulkPromptInput.trim()) {
      savedBulkPromptInput = bulkPromptInput
    }
    showBulkPromptInput = false
  }
  
  // Handle bulk prompt submission for AI mode
  async function handleBulkPromptSubmit() {
    if (!bulkPromptInput.trim()) return
    
    const modelId = currentModelId
    if (!modelId) return
    
    const savedPrompt = bulkPromptInput.trim()
    bulkPromptInput = ''
    savedBulkPromptInput = '' // Clear saved input after submission
    showBulkPromptInput = false
    
    try {
      // Call the onAIPromptAll handler with prompt and model
      if (onAIPromptAll) {
        await onAIPromptAll(savedPrompt, modelId)
      }
    } catch (error) {
      console.error('Failed to generate:', error)
    }
  }
  
  // Handle inline view prompt submission
  async function handleInlineViewPromptSubmit() {
    if (!bulkPromptInput.trim()) return
    
    const modelId = currentModelId
    if (!modelId) return
    
    const savedPrompt = bulkPromptInput.trim()
    bulkPromptInput = ''
    savedBulkPromptInput = '' // Clear saved input after submission
    showBulkPromptInput = false
    
    try {
      // Call the onInlineViewGeneratePrompt for each card in parallel
      if (onInlineViewGeneratePrompt && inlineViewCards) {
        // Create promises for all cards
        const promises = inlineViewCards.map(view => 
          onInlineViewGeneratePrompt(view.id, savedPrompt, modelId)
        )
        
        // Execute all promises in parallel
        await Promise.all(promises)
      }
    } catch (error) {
      console.error('Failed to generate:', error)
    }
  }
  
  // Update AI model
  function updateAIModel(modelId: string) {
    settingsActions.updateAISettings({ selectedModel: modelId })
  }
  
  
  
</script>

<!-- Controllers -->
<AICardController
  bind:this={aiCardController}
  {aiSegments}
  {aiMode}
  {isProcessing}
  {onSegmentHover}
/>

<ErrorCardController
  bind:this={errorCardController}
  {errors}
  {ignoredErrors}
  {isChecking}
  {hasChecked}
  {hasText}
  {allApplied}
  {processedCount}
/>

<ErrorFocusController
  bind:this={errorFocusController}
  bind:errorListContainer
/>

<CompletionController
  bind:this={completionController}
  aiSegments={aiSegments || []}
  {onSegmentHover}
/>

<StatsController
  bind:this={statsController}
  {aiSegments}
/>

<!-- Single Responsibility: Displaying error states and error/AI cards -->
<div
  class="error-cards-container"
  bind:this={errorListContainer}
  role="complementary"
  aria-label="사이드바 - 맞춤법 검사 및 AI 제안"
>
  {#if isAIMode}
    <!-- AI Mode Display -->
    <div class="ai-mode-container">
      <!-- AI Actions Header -->
      <AIActionsHeader
        mode="splitview"
        showStats={true}
        addedChars={totalStats.additions}
        deletedChars={totalStats.deletions}
        isProcessing={isProcessing}
        hasItems={aiSegments.some(s => s.hasChanges)}
        itemsCount={aiSegments.filter(s => s.hasChanges).length}
        showExpand={aiMode !== 'expand'}
        showShrink={aiMode !== 'shrink'}
        showPrompt={!!onAIPromptAll}
        onApplyAll={() => onAIApplyAll?.()}
        onRejectAll={() => {
          onAIRejectAll?.()
          // Close sidebar when all rejected
          import('$lib/stores/sidebarStore').then(({ sidebarStore }) => {
            sidebarStore.close(true) // true = auto-close
          })
        }}
        onContinueAll={() => onAIContinueAll?.()}
        onParaphraseAll={() => onAIParaphraseAll?.()}
        onExpandAll={() => onAIExpandAll?.()}
        onShrinkAll={() => onAIShrinkAll?.()}
        onPromptAll={handleBulkPromptClick}
        showBulkPrompt={showBulkPromptInput && !!onAIPromptAll}
        bind:bulkPromptInput={bulkPromptInput}
        bind:currentModelId={currentModelId}
        onBulkPromptSubmit={handleBulkPromptSubmit}
        onBulkPromptClose={closeBulkPrompt}
        onModelChange={updateAIModel}
      />
      
      <!-- Apply status below header -->
      {#if showApplyMessage}
        <div class="apply-status">
          <span class="apply-status-text">모든 사항이 적용되었습니다. 에디터에 반영할까요?</span>
          <button class="apply-status-btn" onclick={() => {
            onAIApplyAndExit?.()
            completionController?.hideApplyMessage()
          }}>
            적용
          </button>
        </div>
      {/if}
      
      <!-- AI Action Cards -->
      <div class="ai-cards-list">
        {#if isAIAnalyzing}
          <!-- Loading - Only show when actually analyzing -->
          <div class="no-segments-message">
            <div class="loading-spinner"></div>
            <p class="empty-text">AI 분석 중...</p>
            {#if onCancelCheck}
              <button class="cancel-btn" onclick={onCancelCheck}>
                취소
              </button>
            {/if}
          </div>
        {:else if !aiSegments || aiSegments.length === 0}
          <!-- No segments after cancellation or no results -->
          <div class="no-segments-message">
            <p class="empty-text">AI 분석 결과가 없습니다</p>
          </div>
        {:else if showCompletionMessage && (appliedCount > 0 || skippedCount > 0)}
          <!-- Completion Message -->
          <div class="completion-message">
            <div class="completion-icon">
              <CheckCircle size={48} />
            </div>
            <h3>모든 변경사항 처리 완료</h3>
            <p class="completion-stats">
              {appliedCount}개 적용, {skippedCount}개 무시
            </p>
            <div class="completion-actions">
              <button class="btn-primary" onclick={() => {
                // Apply the changes and go back to editor
                onAIApplyAndExit?.()
              }}>
                변경사항 적용하고 에디터로 돌아가기
              </button>
              <button class="btn-secondary" onclick={() => {
                // Hide completion message and show cards again
                completionController?.dismissCompletion()
                onAIContinueEditing?.()
              }}>
                계속 편집하기
              </button>
            </div>
          </div>
        {:else}
          <!-- Show AI Action Cards -->
          {#each aiSegments as segment, index (segment.id)}
            <AIActionCard
              {segment}
              {index}
              mode={aiMode}
              onApply={() => onAIApply?.(segment.id)}
              onIgnore={() => onAIIgnore?.(segment.id)}
              onContinue={() => onAIContinue?.(segment.id)}
              onExpand={() => onAIExpand?.(segment.id)}
              onShrink={() => onAIShrink?.(segment.id)}
              onParaphrase={() => onAIParaphrase?.(segment.id)}
              onPrompt={() => onAIPrompt?.(segment.id)}
              onGeneratePrompt={onAISegmentPromptGenerate ? (prompt, modelId) => onAISegmentPromptGenerate(segment.id, prompt, modelId) : undefined}
              onHover={onSegmentHover}
              onUndo={onSegmentUndo ? () => onSegmentUndo(segment.id) : undefined}
              onRedo={onSegmentRedo ? () => onSegmentRedo(segment.id) : undefined}
              canUndo={canSegmentUndo?.(segment.id) || false}
              canRedo={canSegmentRedo?.(segment.id) || false}
              hideDiffButton={true}
            />
          {/each}
        {/if}
      </div>
    </div>
  {:else if (allApplied && processedCount > 0 && hasText) || displayState === 'all-applied' || displayState === 'all-ignored' || (displayableErrors.length === 0 && inlineViewCards.length === 0)}
    <div class="no-errors-message">
      {#if allApplied && processedCount > 0 && hasText}
        <CheckCircle class="success-icon" />
        <p class="empty-text">모든 오류를 처리했습니다</p>
        <p class="empty-subtext">{processedCount}개 항목 처리 완료</p>
      {:else if displayState === 'all-ignored'}
        <CheckCircle class="success-icon" />
        <p class="empty-text">모든 오류를 무시했습니다</p>
        <p class="empty-subtext">{errors.length}개 항목 무시됨</p>
      {:else if emptyStateMessage}
        {#if emptyStateMessage.icon === 'loading'}
          <div class="loading-spinner"></div>
        {:else if emptyStateMessage.icon === 'success'}
          <svg class="success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        {:else if emptyStateMessage.icon === 'info'}
          <Info class="empty-icon" />
        {:else}
          <Menu class="empty-icon" />
        {/if}
        <p class="empty-text">{emptyStateMessage.title}</p>
        {#if emptyStateMessage.subtitle}
          <p class="empty-subtext">{emptyStateMessage.subtitle}</p>
        {/if}
        {#if emptyStateMessage.icon === 'loading' && isChecking && onCancelCheck}
          <button class="cancel-btn" onclick={onCancelCheck}>
            취소
          </button>
        {/if}
      {:else if hasText && !hasChecked}
        <!-- Show rotating tips when text is entered but not checked yet -->
        <svg class="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
        </svg>
        <p class="empty-text">{tips[currentTipIndex].title}</p>
        <p class="empty-subtext">{tips[currentTipIndex].subtitle}</p>
      {:else if displayableErrors.length === 0 && hasChecked}
        <!-- All errors have been handled (either fixed or ignored) -->
        <CheckCircle class="success-icon" />
        {#if initialErrorCount > 0}
          {@const fixedCount = initialErrorCount - errors.length}
          {@const ignoredCount = ignoredErrors.size}
          {@const keptOriginalCount = errors.filter(e => e.isOriginalKept || e.userAction === 'kept-original').length}
          {#if fixedCount > 0 && (ignoredCount > 0 || keptOriginalCount > 0)}
            <!-- Mixed: some fixed, some ignored/kept -->
            <p class="empty-text">모든 오류를 처리했습니다</p>
            {#if ignoredCount > 0 && keptOriginalCount > 0}
              <p class="empty-subtext">{fixedCount}개 수정, {ignoredCount}개 무시, {keptOriginalCount}개 원본 유지</p>
            {:else if ignoredCount > 0}
              <p class="empty-subtext">{fixedCount}개 수정, {ignoredCount}개 무시</p>
            {:else}
              <p class="empty-subtext">{fixedCount}개 수정, {keptOriginalCount}개 원본 유지</p>
            {/if}
          {:else if ignoredCount === errors.length && errors.length === initialErrorCount}
            <!-- All ignored (no fixes) -->
            <p class="empty-text">모든 오류를 무시했습니다</p>
            <p class="empty-subtext">{ignoredCount}개 항목 무시됨</p>
          {:else if errors.length === 0 && initialErrorCount > 0}
            <!-- All fixed (no ignores) -->
            <p class="empty-text">모든 오류가 수정되었습니다</p>
            <p class="empty-subtext">{initialErrorCount}개 항목 처리 완료</p>
          {:else}
            <p class="empty-text">모든 오류가 처리되었습니다</p>
            <p class="empty-subtext">검사 완료</p>
          {/if}
        {:else}
          <p class="empty-text">모든 오류가 수정되었습니다</p>
          <p class="empty-subtext">검사 완료</p>
        {/if}
      {/if}
    </div>
  {:else}
    <div class="errors-container">
      <!-- Inline View Cards (displayed first if any) -->
      {#if inlineViewCards && inlineViewCards.length > 0}
        <!-- Inline View Actions Toolbar -->
        <AIActionsHeader
          mode="inlineview"
          showStats={true}
          addedChars={inlineViewStats.additions}
          deletedChars={inlineViewStats.deletions}
          isProcessing={inlineViewCards.some(v => v.status === 'processing')}
          hasItems={inlineViewCards.some(v => v.status === 'completed' && v.suggestedText)}
          itemsCount={inlineViewCards.filter(v => v.status === 'completed' && v.suggestedText).length}
          showExpand={true}
          showShrink={true}
          showPrompt={!!onInlineViewGeneratePrompt}
          onApplyAll={() => {
            // Apply all inline views
            inlineViewCards.forEach(view => {
              if (view.status === 'completed' && view.suggestedText) {
                onInlineViewApply?.(view.id)
              }
            })
            
            // Check if sidebar should close after all views are processed
            checkAndCloseSidebar()
          }}
          onRejectAll={() => {
            // Reject all inline views
            inlineViewCards.forEach(view => {
              onInlineViewIgnore?.(view.id)
            })
            
            // Close sidebar immediately when all rejected
            import('$lib/stores/sidebarStore').then(({ sidebarStore }) => {
              sidebarStore.close(true) // true = auto-close
            })
          }}
          onContinueAll={undefined}
          onParaphraseAll={() => {
            inlineViewCards.forEach(view => {
              onInlineViewParaphrase?.(view.id)
            })
          }}
          onExpandAll={() => {
            inlineViewCards.forEach(view => {
              onInlineViewExpand?.(view.id)
            })
          }}
          onShrinkAll={() => {
            inlineViewCards.forEach(view => {
              onInlineViewShrink?.(view.id)
            })
          }}
          onPromptAll={handleBulkPromptClick}
          showBulkPrompt={showBulkPromptInput && !!onInlineViewGeneratePrompt}
          bind:bulkPromptInput={bulkPromptInput}
          bind:currentModelId={currentModelId}
          onBulkPromptSubmit={handleInlineViewPromptSubmit}
          onBulkPromptClose={closeBulkPrompt}
          onModelChange={updateAIModel}
        />
        
        <div class="inline-view-cards-list">
          {#each inlineViewCards as view, index}
            {#if view.segments && view.segments.length > 0 && view.segments[0]}
              <AIActionCard
                segment={{...view.segments[0], viewId: view.id}}
                {index}
                mode={view.mode}
                showIndex={false}
                hideReason={true}
                onApply={() => onInlineViewApply?.(view.id)}
                onIgnore={() => onInlineViewIgnore?.(view.id)}
                onParaphrase={() => onInlineViewParaphrase?.(view.id)}
                onExpand={() => onInlineViewExpand?.(view.id)}
                onShrink={() => onInlineViewShrink?.(view.id)}
                onContinue={() => onInlineViewContinue?.(view.id)}
                onPrompt={() => onInlineViewPrompt?.(view.id)}
                onGeneratePrompt={onInlineViewGeneratePrompt ? (prompt, modelId) => onInlineViewGeneratePrompt(view.id, prompt, modelId) : undefined}
                onUndo={onInlineViewUndo ? () => onInlineViewUndo(view.id) : undefined}
                onRedo={onInlineViewRedo ? () => onInlineViewRedo(view.id) : undefined}
                canUndo={canInlineViewUndo?.(view.id) || false}
                canRedo={canInlineViewRedo?.(view.id) || false}
              />
            {/if}
          {/each}
        </div>
      {/if}
      
      <!-- Error Actions Toolbar for Grammar Check Results -->
      {#if activeErrors.length > 0 && !inlineViewCards?.length}
        <div class="error-actions-toolbar">
          <!-- Apply All -->
          {#if onApplyAll}
            <button
              class="error-action-btn error-action-apply"
              onclick={() => {
                // Cancel any ongoing check first
                if (onCancelCheck) {
                  onCancelCheck()
                }
                onApplyAll()

                // Emit event to restore editor mode (reading mode)
                window.dispatchEvent(new CustomEvent('restore-editor-mode'))
              }}
              title={`모두 적용 (${activeErrors.length}개)`}
              aria-label="모두 적용"
            >
              <Check size={18} />
            </button>
          {/if}
          
          <!-- Reject All -->
          {#if onRejectAll}
            <button
              class="error-action-btn error-action-reject"
              onclick={() => {
                // Cancel any ongoing check first
                if (onCancelCheck) {
                  onCancelCheck()
                }
                onRejectAll()

                // Emit event to restore editor mode
                window.dispatchEvent(new CustomEvent('restore-editor-mode'))

                // Close sidebar when all rejected
                import('$lib/stores/sidebarStore').then(({ sidebarStore }) => {
                  sidebarStore.close(true) // true = auto-close
                })
              }}
              title={`모두 무시 (${activeErrors.length}개)`}
              aria-label="모두 무시"
            >
              <X size={18} />
            </button>
          {/if}
          
          <div class="toolbar-separator"></div>
          
          <!-- Error count -->
          <div class="error-count">
            <span>{activeErrors.length}</span>
          </div>
        </div>
      {/if}
      
      <!-- Errors List -->
      <div class="errors-list">
        {#each errors as error (error.id)}
          {#if !ignoredErrors.has(error.id)}
            <div class="error-card-wrapper">
              <ErrorCard
                {error}
                onApplyCorrection={(correctionId) => onApplyCorrection(error.id, correctionId)}
                onApplyAlternative={(alternative) => onApplyAlternative?.(error.id, alternative)}
                onIgnore={() => onIgnoreError(error.id)}
                onKeepOriginal={() => onKeepOriginal?.(error.id)}
                onMouseEnter={() => onErrorHover?.(error.id)}
                onMouseLeave={() => onErrorUnhover?.()}
              />
            </div>
          {/if}
        {/each}
      </div>
    </div>
  {/if}
</div>

<style>
  .error-cards-container {
    height: 100%;
    overflow-y: auto;
    padding: 12px;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
    will-change: scroll-position;
  }
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  .error-cards-container::-webkit-scrollbar {
    display: none;
  }
  
  .errors-container {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  
  /* Error Actions Toolbar - ultra minimal with sticky positioning */
  .error-actions-toolbar {
    position: sticky;
    top: -12px;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 4px 6px 4px 5px;
    height: 28px;
    margin-bottom: 8px;
    background: var(--bg-primary);
  }
  
  .error-action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    padding: 0;
    border: none;
    border-radius: 3px;
    background: transparent;
    color: var(--text-tertiary);
    cursor: pointer;
    transition: all 0.12s ease;
    position: relative;
  }
  
  .error-action-btn:hover:not(:disabled) {
    background: var(--bg-hover, rgba(0, 0, 0, 0.04));
    color: var(--text-primary);
  }
  
  .error-action-btn:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  .error-action-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  
  .toolbar-separator {
    width: 1px;
    height: 12px;
    background: var(--border-secondary);
    margin: 0 2px;
    opacity: 0.3;
  }
  
  .error-count {
    margin-left: auto;
    margin-right: 2px;
    font-size: 11px;
    color: var(--text-tertiary);
    font-weight: 500;
  }
  
  /* Dark mode for error actions - high contrast */
  :global(.dark-theme) .error-action-btn {
    color: var(--text-secondary);
    background: rgba(255, 255, 255, 0.03);
  }

  :global(.dark-theme) .error-action-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    color: var(--text-primary);
  }

  :global(.dark-theme) .error-actions-toolbar {
    background: var(--bg-primary);
    border-radius: 4px;
  }
  
  /* Auto dark mode (system preference) */
  @media (prefers-color-scheme: dark) {
    :global(:root:not(.light-theme)) .error-action-btn {
      color: var(--text-secondary);
      background: rgba(255, 255, 255, 0.03);
    }

    :global(:root:not(.light-theme)) .error-action-btn:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.1);
      color: var(--text-primary);
    }

    :global(:root:not(.light-theme)) .error-actions-toolbar {
      background: var(--bg-primary);
      border-radius: 4px;
    }
  }
  
  /* Keyboard focus */
  .error-action-btn:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 1px;
  }
  
  .no-errors-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
    color: var(--text-secondary);
    animation: fadeIn 0.5s ease;
  }
  
  .empty-icon,
  .success-icon {
    width: 32px;
    height: 32px;
    margin-bottom: 12px;
    color: var(--text-tertiary);
    stroke-width: 1.5;
  }
  
  .success-icon {
    color: var(--success);
  }
  
  /* Global SVG styling for lucide-svelte icons */
  .completion-icon :global(svg) {
    stroke-width: 1.5;
  }
  
  .success-icon :global(svg),
  .empty-icon :global(svg) {
    width: 32px;
    height: 32px;
    stroke-width: 1.5;
  }
  
  .error-action-btn :global(svg) {
    stroke-width: 1.8;
  }
  
  .empty-text {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-primary);
    margin: 4px 0;
  }
  
  .empty-subtext {
    font-size: 12px;
    color: var(--text-tertiary);
    margin: 2px 0;
  }
  
  .loading-spinner {
    width: 32px;
    height: 32px;
    border: 2px solid var(--border-primary);
    border-top: 2px solid var(--accent-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 12px;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .errors-list {
    display: flex;
    flex-direction: column;
    gap: 0;
    animation: fadeIn 0.3s ease;
    flex: 1;
    padding: 8px 12px 12px 8px;
  }
  
  .inline-view-cards-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 6px 4px;
    border-bottom: 1px solid var(--border-primary);
    margin-bottom: 8px;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .error-card-wrapper {
    margin-bottom: 0;
  }
  
  /* AI Mode Styles */
  .ai-mode-container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  
  /* AI Actions Header styles moved to AIActionsHeader component */
  
  
  .ai-cards-list {
    flex: 1;
    padding: 8px 12px 12px 8px;
  }
  
  .no-segments-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    text-align: center;
    color: var(--text-secondary);
  }
  
  /* Dark mode handled by CSS variables */
  
  /* Completion message */
  .completion-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    padding: 24px;
    text-align: center;
  }
  
  .completion-icon {
    margin-bottom: 16px;
    color: var(--success);
  }
  
  .completion-message h3 {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary);
  }
  
  .completion-stats {
    margin-bottom: 20px;
    padding: 6px 12px;
    background: var(--bg-secondary);
    border-radius: 6px;
    font-size: 13px;
    color: var(--text-secondary);
  }
  
  .completion-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }
  
  .btn-primary,
  .btn-secondary {
    padding: 8px 14px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    border: none;
  }
  
  .btn-primary {
    background: var(--accent-primary);
    color: white;
  }
  
  .btn-primary:hover {
    background: var(--accent-secondary);
  }
  
  .btn-secondary {
    background: transparent;
    color: var(--text-secondary);
    border: 1px solid var(--border-primary);
  }
  
  .btn-secondary:hover {
    background: var(--bg-hover);
  }
  
  /* Apply status - minimal style below header */
  .apply-status {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    margin: 0 0 8px 0;
    background: rgba(34, 197, 94, 0.08);
    font-size: 13px;
  }
  
  .apply-status-text {
    color: var(--success);
    font-weight: 500;
    flex: 1;
  }
  
  .apply-status-btn {
    background: transparent;
    color: var(--success);
    border: 1px solid var(--success);
    border-radius: 4px;
    padding: 3px 14px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
  }
  
  .apply-status-btn:hover {
    background: var(--success);
    color: white;
  }
  
  /* Dark theme */
  :global(.dark-theme) .apply-status {
    background: rgba(34, 197, 94, 0.1);
  }
  
  /* Bulk prompt styles moved to BulkPromptInput.svelte */
  
  /* Cancel button */
  .cancel-btn {
    margin-top: 12px;
    padding: 6px 16px;
    background: transparent;
    color: var(--text-secondary);
    border: 1px solid var(--border-secondary);
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  
  .cancel-btn:hover {
    background: var(--bg-hover);
    border-color: var(--border-primary);
    color: var(--text-primary);
  }
  
  .cancel-btn:active {
    transform: scale(0.98);
  }
</style>